import {
  createAsyncThunk,
  createEntityAdapter,
  createSlice,
  EntityId,
} from '@reduxjs/toolkit';
import { deleteJSON, postJSON, putJSON } from 'api/fetch';
import { RootState } from 'initializers/types';

import { fetchSchedule } from 'features/scheduleBuilder/ScheduleBuilderView/actions/shared';

import { EventQuotaSerializedEntity } from '../eventQuotas/types';
import { captureHydrationException } from '../helpers';

import { SLICE_NAME } from './constants';
import type {
  ApiEventResponse,
  CreateEventResponse,
  Event,
  EventSerializedEntity,
  EventsState,
  SelectEventAttributeArgs,
} from './types';

export const eventsAdapter = createEntityAdapter<Event>();

const initialState: EventsState = {
  ...eventsAdapter.getInitialState(),
  status: 'idle',
  error: undefined,
};

export const createEvent = createAsyncThunk<
  CreateEventResponse,
  { payload: Omit<Event, 'id'> },
  { rejectValue: unknown }
>(`${SLICE_NAME}/createEvent`, async ({ payload }, { rejectWithValue }) => {
  try {
    const response = await postJSON('/schedule_builder/events', {
      data: { attributes: payload },
    });

    return response;
  } catch (error) {
    return rejectWithValue(error);
  }
});

export const updateEvent = createAsyncThunk<
  ApiEventResponse,
  { payload: Event },
  { rejectValue: unknown }
>(`${SLICE_NAME}/updateEvent`, async ({ payload }, { rejectWithValue }) => {
  try {
    const { id, ...attributes } = payload;

    const response = await putJSON(`/schedule_builder/events/${id}`, {
      data: { id, attributes },
    });

    return response.data;
  } catch (error) {
    return rejectWithValue(error);
  }
});

export const deleteEvent = createAsyncThunk<
  EventQuotaSerializedEntity,
  { id: EntityId },
  { rejectValue: unknown }
>(`${SLICE_NAME}/deleteEvent`, async ({ id }, { rejectWithValue }) => {
  try {
    const response = await deleteJSON(`/schedule_builder/events/${id}`);
    return response.data;
  } catch (error) {
    return rejectWithValue(error);
  }
});

export const slice = createSlice({
  name: SLICE_NAME,
  initialState,
  reducers: {
    setAll: eventsAdapter.setAll,
    removeAll: eventsAdapter.removeAll,
  },
  extraReducers: builder => {
    builder
      .addCase(fetchSchedule.fulfilled, (state, action) => {
        try {
          state.status = 'succeeded';
          if (action.payload?.included) {
            const events: Event[] = (
              action.payload.included as EventSerializedEntity[]
            )
              .filter(
                (entity: EventSerializedEntity) => entity.type === 'event'
              )
              .map((entity: EventSerializedEntity) => ({
                id: entity.id,
                title: entity.attributes.title,
                description: entity.attributes.description,
                holiday: entity.attributes.holiday,
                date: entity.attributes.date,
              }));
            eventsAdapter.setAll(state, events);
          }
        } catch (error) {
          captureHydrationException(state, error, SLICE_NAME);
        }
      })
      .addCase(createEvent.pending, state => {
        state.status = 'pending';
        state.error = undefined;
      })
      .addCase(createEvent.fulfilled, (state, action) => {
        state.status = 'succeeded';

        if (action.payload) {
          const eventData = action.payload.data;

          const newEvent: Event = {
            id: eventData.id,
            title: eventData.attributes.title,
            description: eventData.attributes.description,
            holiday: eventData.attributes.holiday,
            date: eventData.attributes.date,
          };

          eventsAdapter.addOne(state, newEvent);
        }
      })
      .addCase(createEvent.rejected, (state, action) => {
        state.status = 'failed';
        state.error = (action.payload as string) || 'Failed to create event';
      })
      .addCase(deleteEvent.pending, state => {
        state.status = 'pending';
        state.error = undefined;
      })
      .addCase(deleteEvent.fulfilled, (state, action) => {
        eventsAdapter.removeOne(state, action.meta.arg.id);
        state.status = 'succeeded';
      })
      .addCase(deleteEvent.rejected, (state, action) => {
        state.status = 'failed';
        state.error = (action.payload as string) || 'Failed to delete event';
      })
      .addCase(updateEvent.pending, state => {
        state.status = 'pending';
        state.error = undefined;
      })
      .addCase(updateEvent.fulfilled, (state, action) => {
        state.status = 'succeeded';

        if (action.payload) {
          const eventData = action.payload;

          const updatedEvent: Event = {
            id: eventData.id,
            title: eventData.attributes.title,
            description: eventData.attributes.description,
            holiday: eventData.attributes.holiday,
            date: eventData.attributes.date,
          };

          eventsAdapter.updateOne(state, {
            id: updatedEvent.id,
            changes: updatedEvent,
          });
        }
      })
      .addCase(updateEvent.rejected, (state, action) => {
        state.status = 'failed';
        state.error = (action.payload as string) || 'Failed to update event';
      });
  },
});

export const { setAll, removeAll } = slice.actions;

export const { selectAll, selectById } = eventsAdapter.getSelectors<RootState>(
  state => state.get('scheduleBuilderSchedule').events
);

export const selectEventAttribute = ({
  state,
  attribute,
  eventId,
}: SelectEventAttributeArgs): Event[keyof Event] | undefined => {
  const event = selectById(state, eventId);
  return event ? event[attribute] : undefined;
};

export const { reducer, actions } = slice;
